import { useLayoutEffect, useState } from "react";

// The repo's TypeScript 4.0 DOM types predate ResizeObserver, so it is typed here and looked up at runtime.
type ResizeObserverLike = { observe: (target: Element) => void; disconnect: () => void };
const resizeObserverClass = () =>
  (window as unknown as { ResizeObserver?: new (callback: () => void) => ResizeObserverLike }).ResizeObserver;

const bottomOf = (element: HTMLElement) =>
  (parseFloat(window.getComputedStyle(element).top) || 0) + element.getBoundingClientRect().height;

/**
 * Where a sticky header ends, so content below it can stick right underneath instead of sliding behind it.
 * Measured when the calling component mounts, then kept current as the header's height changes (status
 * actions, banners) where ResizeObserver exists.
 */
export const useStickyBottom = (getHeader: () => HTMLElement | null | undefined): number => {
  const [bottom, setBottom] = useState(0);

  useLayoutEffect(() => {
    const header = getHeader();
    if (!header) return;
    const measure = () => setBottom(bottomOf(header));
    measure();
    const ResizeObserverClass = resizeObserverClass();
    if (ResizeObserverClass) {
      const observer = new ResizeObserverClass(measure);
      observer.observe(header);
      return () => observer.disconnect();
    }
    // Older browsers: re-measure on resize, and briefly while the header's own content finishes loading.
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    const settle = window.setInterval(measure, 500);
    const stopSettling = window.setTimeout(() => window.clearInterval(settle), 10000);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      window.clearInterval(settle);
      window.clearTimeout(stopSettling);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return bottom;
};
